/**
 * @file     ProposalCard.tsx
 * @purpose  The confirmation card for a propose-class agent action: what will
 *           happen, exact parameters, Confirm / Not now. Confirm executes
 *           server-side through the actions route (CSRF, RLS, idempotent) and
 *           the outcome renders as a status line.
 */
'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import type { AgentProposal } from '@/services/ai-chat/capabilities/types';
import { displayableCount } from '@/features/property-finder/counts';

type Status = 'proposed' | 'working' | 'executed' | 'failed' | 'dismissed' | 'unknown';

/** Outcomes the platform could not confirm. They are shown as unknown and are never offered for another Confirm. */
const UNKNOWN_CODES = ['unknown', 'manual_review'];

export function ProposalCard({
  proposal,
  onConfirm,
  onDismiss,
  initialStatus = 'proposed',
  initialResultText = '',
  readOnly = false,
}: {
  proposal: AgentProposal;
  /** Custom confirm handler (e.g. the admin Relta agent's confirm route). When
   *  omitted, the buyer chat's /api/ai-chat/actions route is used. */
  onConfirm?: (proposal: AgentProposal) => Promise<{ ok: boolean; resultText?: string; errorCode?: string }>;
  /** Called on "Not now" so the host can persist the dismissal; the local
   *  dismiss is optimistic and stands regardless. */
  onDismiss?: (proposal: AgentProposal) => void;
  /** Persisted status for a rehydrated proposal, so an already-executed action
   *  renders its outcome instead of offering Confirm again. */
  initialStatus?: Status;
  initialResultText?: string;
  /** Show the proposal as context only (e.g. another admin's pending action):
   *  no Confirm/Dismiss controls, so it is never actioned from here. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [status, setStatus] = useState<Status>(initialStatus);
  const [resultText, setResultText] = useState(initialResultText);
  const [failCode, setFailCode] = useState<string | null>(null);

  const confirm = async () => {
    setStatus('working');
    setFailCode(null);
    try {
      if (onConfirm) {
        const out = await onConfirm(proposal);
        if (out.ok) {
          setResultText(out.resultText ?? '');
          setStatus('executed');
        } else {
          setFailCode(out.errorCode ?? null);
          setStatus(out.errorCode && UNKNOWN_CODES.includes(out.errorCode) ? 'unknown' : 'failed');
        }
        return;
      }
      const res = await secureFetch('/api/ai-chat/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: proposal.id }),
      });
      const data = (await res.json().catch(() => ({}))) as { status?: string; resultText?: string; error?: string };
      if (res.ok && data.status === 'executed') {
        setResultText(data.resultText ?? '');
        setStatus('executed');
      } else {
        setFailCode(data.error ?? null);
        setStatus(data.error && UNKNOWN_CODES.includes(data.error) ? 'unknown' : 'failed');
      }
    } catch {
      setStatus('failed');
    }
  };

  const failedMessage = () => {
    switch (failCode) {
      case 'expired':
        return t('aiChat.proposalFailedExpired', 'This action expired. Ask Relta to prepare it again.');
      case 'forbidden':
        return t('aiChat.proposalFailedForbidden', 'You do not have permission to run this action.');
      case 'not_found':
        return t('aiChat.proposalFailedNotFound', 'This action no longer exists. Ask Relta to prepare it again.');
      case 'unknown':
      case 'manual_review':
        return t('aiChat.proposalFailedUnknown', 'Relta could not confirm whether this went through. Check the record before you try anything again. It will not be repeated.');
      case 'in_progress':
      case 'executing':
      case 'already_claimed':
        return t('aiChat.proposalFailedInProgress', 'This action is already running. Check again in a moment.');
      case 'not_confirmable':
        return t('aiChat.proposalFailedNotConfirmable', 'This action cannot be confirmed from here.');
      default:
        return t('aiChat.proposalFailed', 'That did not go through. Try again from the card.');
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        px: 2,
        py: 2,
        borderRadius: 1,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        backgroundColor: theme.semanticColors.surfaceCard,
        maxWidth: 440,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: theme.semanticColors.textPrimary }}>
        {proposal.summary}
      </Typography>
      {Object.entries(proposal.details).map(([k, v]) => (
        <Typography key={k} sx={{ fontSize: 12.5, color: theme.semanticColors.textSecondary, mt: 0.25 }}>
          {k}: {v}
        </Typography>
      ))}
      {proposal.preview && (
        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.semanticColors.borderSubtle}` }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: proposal.preview.count === 0 ? theme.semanticColors.statusWarning : theme.semanticColors.textPrimary,
            }}
          >
            {proposal.preview.count === 0
              ? t('reltaAgent.previewEmpty', 'No homes match right now')
              : displayableCount(proposal.preview.count) == null
                ? t('reltaAgent.previewLarge', 'Homes that fit')
                : t('reltaAgent.previewCount', '{{count}} homes match right now', { count: proposal.preview.count })}
          </Typography>
          {proposal.preview.note && (
            <Typography sx={{ fontSize: 12, color: theme.semanticColors.textMuted, mt: 0.25 }}>
              {proposal.preview.note}
            </Typography>
          )}
          {proposal.preview.items.map((item, i) => (
            <Box key={i} sx={{ mt: 0.5 }}>
              <Typography
                component={item.href ? 'a' : 'span'}
                {...(item.href ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : {})}
                sx={{
                  fontSize: 12.5,
                  color: item.href ? theme.semanticColors.interactivePrimary : theme.semanticColors.textPrimary,
                  textDecoration: 'none',
                  '&:hover': item.href ? { textDecoration: 'underline' } : undefined,
                }}
              >
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography component="span" sx={{ fontSize: 12, color: theme.semanticColors.textMuted }}>
                  {'  '}· {item.subtitle}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
      {status === 'proposed' && !readOnly && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="contained" size="small" onClick={() => void confirm()} sx={{ borderRadius: 1, textTransform: 'none', fontSize: 13 }}>
            {t('aiChat.proposalConfirm', 'Confirm')}
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setStatus('dismissed');
              onDismiss?.(proposal);
            }}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: 13, color: theme.semanticColors.textSecondary }}
          >
            {t('aiChat.proposalDismiss', 'Not now')}
          </Button>
        </Box>
      )}
      {status === 'proposed' && readOnly && (
        <Typography sx={{ fontSize: 12.5, color: theme.semanticColors.textMuted, mt: 1 }}>
          {t('aiChat.proposalPendingOther', 'Pending, awaiting confirmation.')}
        </Typography>
      )}
      {status === 'working' && <CircularProgress size={16} thickness={5} sx={{ mt: 1 }} />}
      {status === 'executed' && (
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: theme.semanticColors.statusSuccess, mt: 1 }}>
          {resultText || t('aiChat.proposalDone', 'Done.')}
        </Typography>
      )}
      {status === 'failed' && (
        <Typography sx={{ fontSize: 12.5, color: theme.semanticColors.statusError, mt: 1 }}>
          {failedMessage()}
        </Typography>
      )}
      {status === 'unknown' && (
        <Typography role="status" sx={{ fontSize: 12.5, color: theme.semanticColors.statusWarning, mt: 1 }}>
          {t('aiChat.proposalFailedUnknown', 'Relta could not confirm whether this went through. Check the record before you try anything again. It will not be repeated.')}
        </Typography>
      )}
      {status === 'dismissed' && (
        <Typography sx={{ fontSize: 12.5, color: theme.semanticColors.textMuted, mt: 1 }}>
          {t('aiChat.proposalDismissed', 'Skipped.')}
        </Typography>
      )}
    </Box>
  );
}
